import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/api';
import {
  Search, Code, GitPullRequest, GitMerge, MessageSquare,
  Loader2, AlertCircle, ExternalLink, Filter, X,
  FileCode, Folder, CheckCircle, Clock, User,
  Hash, ChevronDown, ChevronRight, Copy, Zap,
  Database, Settings, History, ArrowRight
} from 'lucide-react';
import HistoryPanel from '@/HistoryPanel';
import {
  createSearchHistory,
  updateSearchHistory,
} from '@/services/historyService';

import { cn } from '@/lib/utils'; // Assuming this exists or I should use standard className

const SOURCE_CONFIG = {
  code: {
    icon: Code,
    color: 'teal',
    label: 'Code',
    bgColor: 'bg-primary/10',
    textColor: 'text-primary',
    borderColor: 'border-primary/20'
  },
  github: {
    icon: GitPullRequest, // Using Lucide icon instead of custom SVG for consistency
    color: 'gray',
    label: 'GitHub',
    bgColor: 'bg-secondary',
    textColor: 'text-foreground',
    borderColor: 'border-border'
  },
  slack: {
    icon: MessageSquare, // Using Lucide icon
    color: 'pink',
    label: 'Slack',
    bgColor: 'bg-info/10',
    textColor: 'text-info',
    borderColor: 'border-info/20'
  }
};

export default function UnifiedSearch({
  onNavigateToIntegrations,
  onNavigateToInvestigation,
  onInvestigate,
  pendingSearchRestore,
  onSearchRestored
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState(null);
  const [selectedSources, setSelectedSources] = useState(['code', 'github', 'slack']);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedResults, setExpandedResults] = useState({});
  const [copiedCode, setCopiedCode] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const currentSearchId = useRef(null);

  // Load available sources on mount
  useEffect(() => {
    loadSources();
  }, []);

  // Handle pending search restore from global history
  useEffect(() => {
    if (pendingSearchRestore) {
      handleRestoreSearch(pendingSearchRestore);
      onSearchRestored?.();
    }
  }, [pendingSearchRestore]);

  const loadSources = async () => {
    try {
      const response = await api.get('/search/sources');
      setSources(response.data);
    } catch (err) {
      console.warn('Failed to load sources, using defaults', err);
      // Fallback for UI if API fails
      setSources({ sources: [], total_items: 0 });
    }
  };

  const handleSearch = async (e, queryOverride = null, sourcesOverride = null) => {
    e?.preventDefault();
    const searchQuery = queryOverride || query;
    const searchSources = sourcesOverride || selectedSources;

    if (!searchQuery.trim()) return;

    // Check if query is a question
    const isQuestion = (q) => {
      const trimmed = q.trim().toLowerCase();
      if (trimmed.includes('?')) return true;
      const questionStarters = ['what', 'why', 'how', 'when', 'where', 'which', 'who', 'is', 'are', 'can', 'could', 'would', 'should', 'does', 'do', 'will', 'explain', 'describe', 'tell me', 'help me', 'investigate', 'analyze', 'find out'];
      return questionStarters.some(starter => trimmed.startsWith(starter + ' ') || trimmed === starter);
    };

    if (isQuestion(searchQuery) && onInvestigate) {
      onInvestigate(searchQuery);
      return;
    }

    setLoading(true);
    setError(null);
    setExpandedResults({}); // Reset expanded

    // Create history record at start
    const startTime = Date.now();
    let historyId = null;
    try {
      const historyResult = await createSearchHistory({
        queryText: searchQuery.trim(),
        filters: { sources: searchSources },
        dataSource: 'unified',
      });
      historyId = historyResult.id;
      currentSearchId.current = historyId;
    } catch (histErr) {
      console.warn('Failed to create search history:', histErr);
    }

    try {
      const response = await api.post('/search', {
        query: searchQuery.trim(),
        sources: searchSources,
        limit: 20
      });
      setResults(response.data);

      // Update history
      if (historyId) {
        const latencyMs = Date.now() - startTime;
        try {
          await updateSearchHistory(historyId, {
            resultMeta: {
              totalHits: response.data.total_results || 0,
              latencyMs,
              sourcesSearched: response.data.sources_searched || searchSources,
              errors: response.data.errors,
            },
          });
        } catch (updateErr) {
          // ignore
        }
      }
    } catch (err) {
      console.error('Search failed:', err);
      setError(err.response?.data?.detail || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreSearch = (historyItem) => {
    const queryText = historyItem.queryText || historyItem.query_text;
    const sources = historyItem.filters?.sources || selectedSources;

    setQuery(queryText);
    setSelectedSources(sources);

    setTimeout(() => {
      handleSearch(null, queryText, sources);
    }, 0);
  };

  const toggleSource = (sourceId) => {
    setSelectedSources(prev => {
      if (prev.includes(sourceId)) {
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== sourceId);
      }
      return [...prev, sourceId];
    });
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const hasResults = results && results.results && results.results.length > 0;
  const isInitialState = !results && !loading && !error;

  return (
    <div className={`transition-all duration-500 ease-in-out flex flex-col ${isInitialState ? 'h-[80vh] justify-center max-w-3xl mx-auto' : 'space-y-6'}`}>

      {/* Hero Header (Initial State) */}
      {isInitialState && (
        <div className="text-center mb-10 animate-fade-in-up">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary/20">
            <Database className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground mb-3 text-display">
            Vedaya Intelligence
          </h1>
          <p className="text-lg text-muted-foreground">
            Search across your entire engineering knowledge graph
          </p>
        </div>
      )}

      {/* Search Input Area */}
      <div className={`w-full relative z-20 transition-all duration-500 ${isInitialState ? 'mb-8' : ''}`}>
        <form onSubmit={handleSearch} className="relative group">
          <div className={`absolute inset-0 bg-gradient-to-r from-primary/20 via-sky-400/20 to-primary/20 rounded-xl blur-xl opacity-0 transition-opacity duration-500 ${loading ? 'opacity-100' : 'group-hover:opacity-40'}`} />

          <div className="relative bg-card rounded-xl border border-border shadow-soft-lg overflow-hidden transition-all duration-200 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
            <div className="flex items-center px-4 py-4">
              <Search className={`w-5 h-5 mr-3 transition-colors ${loading ? 'text-primary animate-pulse' : 'text-muted-foreground'}`} />

              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask anything... (e.g. 'Why is checkout latency high?', 'Show me P0 incidents')"
                className="flex-1 bg-transparent border-none outline-none text-lg text-foreground placeholder:text-muted-foreground/60"
                autoFocus={isInitialState}
              />

              <div className="flex items-center gap-2 pl-2">
                <button
                  type="button"
                  onClick={() => setShowHistory(true)}
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  title="Search History"
                >
                  <History className="w-5 h-5" />
                </button>
                <div className="h-6 w-px bg-border mx-1" />
                <button
                  type="submit"
                  disabled={!query.trim() || loading}
                  className="p-2 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Quick Filters / Context Bar */}
            <div className="flex items-center gap-4 px-4 py-2.5 bg-secondary/30 border-t border-border/50 text-xs text-muted-foreground">
              <span className="font-medium">Search in:</span>
              <div className="flex items-center gap-3">
                {['code', 'github', 'slack'].map(src => {
                  const isSelected = selectedSources.includes(src);
                  return (
                    <button
                      key={src}
                      type="button"
                      onClick={() => toggleSource(src)}
                      className={`flex items-center gap-1.5 transition-colors ${isSelected ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${isSelected ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                      {src.charAt(0).toUpperCase() + src.slice(1)}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Suggested Queries (Initial State) */}
      {isInitialState && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          {[
            { label: 'Find connection pool config', icon: Code },
            { label: 'Recent P0 incidents', icon: AlertCircle },
            { label: 'Checkout service architecture', icon: Database },
            { label: 'Database migration errors', icon: TerminalIcon => <FileCode className="w-4 h-4" /> } // Fix icon
          ].map((item, idx) => (
            <button
              key={idx}
              onClick={() => {
                setQuery(item.label);
                handleSearch(null, item.label);
              }}
              className="flex items-center gap-3 p-3.5 bg-card hover:bg-secondary border border-border hover:border-border/80 rounded-lg text-left transition-all text-sm group"
            >
              <div className="w-8 h-8 rounded-lg bg-secondary group-hover:bg-white flex items-center justify-center text-muted-foreground group-hover:text-primary transition-colors">
                {typeof item.icon === 'function' ? item.icon() : <item.icon className="w-4 h-4" />}
              </div>
              <span className="text-foreground/80 group-hover:text-foreground font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Results View */}
      {results && (
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between pb-2 border-b border-border">
            <div className="text-sm text-foreground font-medium">
              Found {results.total_results} results ({results.results?.length} shown)
            </div>
            <div className="text-xs text-muted-foreground">
              {results.sources_searched?.join(', ')}
            </div>
          </div>

          {results.results.map((result, idx) => (
            <div
              key={result.id || idx}
              className="group card p-5 hover:border-primary/40 transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                {/* Icon Box */}
                <div className="mt-1 w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0 border border-border group-hover:border-primary/20 group-hover:bg-primary/5 transition-colors">
                  {result.type === 'code' && <Code className="w-5 h-5 text-teal-600" />}
                  {result.type === 'pr' && <GitPullRequest className="w-5 h-5 text-purple-600" />}
                  {result.type === 'issue' && <AlertCircle className="w-5 h-5 text-amber-600" />}
                  {result.type === 'slack_thread' && <MessageSquare className="w-5 h-5 text-pink-600" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="font-semibold text-foreground truncate pr-4 text-base group-hover:text-primary transition-colors">
                      {result.title}
                    </h3>
                    <span className="text-xs font-mono text-muted-foreground bg-secondary px-2 py-0.5 rounded border border-border">
                      {result.score ? Math.round(result.score * 100) + '%' : ''} match
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                    {result.description}
                  </p>

                  {/* Metadata Row */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      {result.metadata?.repo && <Database className="w-3.5 h-3.5" />}
                      <span>{result.metadata?.repo}</span>
                    </div>
                    {result.metadata?.author && (
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5" />
                        <span>{result.metadata.author}</span>
                      </div>
                    )}
                    {result.url && (
                      <a href={result.url} target="_blank" rel="noopener" className="flex items-center gap-1 text-primary hover:underline ml-auto">
                        Open <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>

                  {/* Code Snippet (if applicable) */}
                  {result.type === 'code' && result.metadata?.source_code && (
                    <div className="mt-4 relative bg-secondary/50 rounded-lg border border-border overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-1.5 bg-secondary border-b border-border text-xs text-muted-foreground">
                        <span className="font-mono">{result.metadata.file_path}</span>
                        <button onClick={() => copyToClipboard(result.metadata.source_code, result.id)} className="hover:text-foreground">
                          {copiedCode === result.id ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <pre className="p-3 overflow-x-auto text-xs font-mono leading-relaxed">
                        {result.metadata.source_code}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History Slide-over */}
      <HistoryPanel
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        onRestoreSearch={handleRestoreSearch}
        onNavigateToInvestigation={onNavigateToInvestigation}
      />
    </div>
  );
}
